import React, { useContext, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { IconButton, Tooltip } from '@mui/material';
import { Modal } from '@/components/_common';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { selectFileTypeGroupings } from '@/redux/selectors/constantsSelectors';
import { selectActiveTitleId, selectActiveVersions } from '@/redux/selectors/titleSelectors';
import { selectActiveTitleId as selectActiveTitleIdV2, selectActiveVersions as selectActiveVersionsV2 } from '@/redux/selectors/titleV2Selectors';
import { clearTitle, getTitleDetails } from '@/redux/slices/titleSlice';
import { clearTitleSlice as clearTitleV2, fetchTitleV2 as getTitleDetailsV2 } from '@/redux/slices/titleV2Slice';
import { setFilters, acceptVersionsFromTitleSlice, resetFilters } from '@/redux/slices/orderPlacementSlice';
import ModalFooterButtons from '@/components/_common/Modal/ModalFooterButtons';
import { ConfigContext } from '@/contextAPI/configContext';
import { useToast } from '@/components/_common/Toast';
import { ContentPlaybackTimelineAudioTrack, FileWithProxy } from './video-player.types';
import SelectedFilesSidebar from './SelectedFilesSidebar';
import InventoryFilesList from './InventoryFilesList';
import { selectInventorySelections } from '@/redux/selectors/orderPlacementSelectors';
import useContentPlayback from './useContentPlayback';

interface Props {
  show: boolean;
  videoUrl?: string | null;
  onClose: () => void;
  onUpdateFiles: (files: FileWithProxy[], videoUrlFromDolby?: string, audioTracks?: ContentPlaybackTimelineAudioTrack[]) => void;
}

const InventoryModal: React.FC<Props> = ({ show, videoUrl, onClose, onUpdateFiles }) => {
  const isTitleV2 = useContext(ConfigContext)?.titleConfig?.v2;
  const dispatch = useAppDispatch();
  const fileTypeGroupings = useAppSelector(selectFileTypeGroupings);
  const selectedFiles = useAppSelector(selectInventorySelections);
  const [loading, setLoading] = useState(false);
  const { getPlaybackSourceUrlsAsFiles } = useContentPlayback();

  const activeTitleId = useAppSelector(isTitleV2 ? selectActiveTitleIdV2 : selectActiveTitleId);
  const activeVersions = useAppSelector(isTitleV2 ? selectActiveVersionsV2 : selectActiveVersions);
  const { error } = useToast();

  useEffect(() => {
    if (activeTitleId !== null && activeVersions && activeVersions.length > 0) {
      dispatch(acceptVersionsFromTitleSlice({ versions: activeVersions, id: activeTitleId }));
    }
  }, [activeTitleId, activeVersions]);

  useEffect(() => {
    if (!activeTitleId) {
      return;
    }

    if (isTitleV2) {
      dispatch(getTitleDetailsV2(activeTitleId));
    } else {
      dispatch(getTitleDetails(activeTitleId));
    }
  }, [activeTitleId]);

  const cleanUp = () => {
    dispatch(resetFilters());
  };

  const unmountCleanUp = () => {
    cleanUp();
    if (isTitleV2) {
      dispatch(clearTitleV2());
    } else {
      dispatch(clearTitle());
    }
  };

  useEffect(() => {
    const { audio, subtitle } = fileTypeGroupings;
    if (show) {
      dispatch(
        setFilters({
          version_name_filter: [],
          file_type_filter: [...audio, ...subtitle]
        })
      );
    }
  }, [fileTypeGroupings, show]);

  useEffect(() => () => unmountCleanUp(), []);

  useEffect(() => {
    if (!show) {
      unmountCleanUp();
    }
  }, [show]);

  const handleUpdateFiles = async () => {
    try {
      if (!videoUrl) {
        error('No video url provided.');
        return;
      }

      if (!activeTitleId) {
        error('Please select a title.');
        return;
      }

      setLoading(true);
      const { filesWithProxy, videoUrlFromDolby, audioTracks } = await getPlaybackSourceUrlsAsFiles(selectedFiles, activeTitleId, videoUrl);

      onUpdateFiles(filesWithProxy, videoUrlFromDolby, audioTracks);
    } catch {
      error('Error fetching file.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <StyledModal open={show} onClose={onClose} title="Inventory Assignment" noHeader scrollDisabled>
      <InnerWrapper>
        <SelectedFilesSidebar />
        <Content>
          <MainContent>
            <InventoryFilesList disableFileSelection={loading} />
          </MainContent>
          <StyledFooter>
            <ModalFooterButtons
              submit={{
                text: 'Update Files',
                onClick: handleUpdateFiles,
                disabled: loading
              }}
              cancel={onClose}
            />
          </StyledFooter>
        </Content>
        <Tooltip title="Close modal">
          <CloseButtonWrapper>
            <CloseButton onClick={onClose}>
              <PolarisIcon icon="close" />
            </CloseButton>
          </CloseButtonWrapper>
        </Tooltip>
      </InnerWrapper>
    </StyledModal>
  );
};

export default InventoryModal;

const StyledModal = styled(Modal)`
  > .MuiPaper-root {
    width: 55vw;
    height: 60vh;
    max-width: none;
    overflow: hidden;
    padding: 0;
    background: ${({ theme }) =>
      theme.palette.mode === 'dark'
        ? `
     radial-gradient(
        107.39% 121.2% at 65.13% -30.51%,
        rgba(125, 138, 243, 0.5) 13.92%,
        rgba(69, 83, 196, 0.5) 43%,
        rgba(57, 69, 162, 0.5) 62%,
        rgba(33, 40, 94, 0.5) 100%
      ),
      rgba(27, 29, 39, 1)`
        : theme.palette.background.paper};
  }

  .MuiBox-root {
    padding: 0;
    max-height: none;
    height: 100%;

    .MuiPaper-root {
      background: transparent;
      box-shadow: none;
    }
  }

  .MuiAccordion-root.MuiPaper-root {
    background: transparent;
    min-height: 50px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;

    .MuiAccordionSummary-root {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: center;
      width: 100%;
    }
  }
`;

const InnerWrapper = styled.div`
  display: flex;
  flex-direction: row;
  height: 100%;
  width: 100%;
  position: relative;
  overflow: hidden;
`;

const Content = styled.div`
  flex-grow: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const MainContent = styled.div`
  flex-grow: 1;
  min-width: 0;
  min-height: 0;
  display: flex;

  .title-search-loading-grid {
    max-width: 100%;
  }
`;

const StyledFooter = styled.div`
  background-color: ${({ theme }) => theme.palette.background.paper + '7a'};

  > div {
    border-top: none;
  }
`;

const CloseButtonWrapper = styled.div`
  position: absolute;
  top: ${({ theme }) => theme.spacing(1)};
  right: ${({ theme }) => theme.spacing(2)};
`;

const CloseButton = styled(IconButton)`
  opacity: 0.5;
  transition: ease opacity 250ms;
  &:hover {
    opacity: 1;
  }
`;
